import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
    createAttribute,
    createAttributeValue,
    deleteAttribute,
    deleteAttributeValue,
    getAttributes,
    getAttributeValues,
    updateAttribute,
    updateAttributeValue,
} from '../../../api/service.api';
import styles from '../../../assets/scss/pages/AttributeList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

export default function AttributeList() {
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission(PERMISSIONS.ATTRIBUTE.CREATE);
    const canUpdate = hasPermission(PERMISSIONS.ATTRIBUTE.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.ATTRIBUTE.DELETE);

    const [attributes, setAttributes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toastMsg, setToastMsg] = useState(null);

    // Panel: attribute detail
    const [selectedAttrId, setSelectedAttrId] = useState(null);
    const [attrName, setAttrName] = useState('');
    const [attrValues, setAttrValues] = useState([]);
    const [loadingValues, setLoadingValues] = useState(false);
    const [savingName, setSavingName] = useState(false);
    const [newValue, setNewValue] = useState('');
    const [editingValueId, setEditingValueId] = useState(null);
    const [editingValueText, setEditingValueText] = useState('');

    // Create modal
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createValues, setCreateValues] = useState(['']);
    const [createError, setCreateError] = useState('');
    const [creating, setCreating] = useState(false);

    const showToast = useCallback((type, text, duration = 1000) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    // === Fetch attributes list ===
    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getAttributes();
            setAttributes(data.attributes || []);
        } catch {
            setAttributes([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    // === Fetch attribute values (panel) ===
    const fetchValues = useCallback(async (attrId) => {
        setLoadingValues(true);
        try {
            const { data } = await getAttributeValues(attrId);
            const vals = data.attributeValues || [];
            setAttrValues(vals);
            if (vals.length > 0 && vals[0].attribute?.name) {
                setAttrName(vals[0].attribute.name);
            }
        } catch {
            setAttrValues([]);
        } finally {
            setLoadingValues(false);
        }
    }, []);

    const openPanel = (attr) => {
        setSelectedAttrId(attr.id);
        setAttrName(attr.name);
        setAttrValues([]);
        setNewValue('');
        setEditingValueId(null);
        fetchValues(attr.id);
    };

    const closePanel = () => {
        setSelectedAttrId(null);
        setEditingValueId(null);
    };

    // === Delete attribute ===
    const handleDeleteAttribute = async (e, attrId) => {
        e.stopPropagation();
        if (!canDelete) return;
        if (!window.confirm('Bạn có chắc muốn xoá thuộc tính này?')) return;
        try {
            await deleteAttribute(attrId);
            showToast('success', 'Xoá thuộc tính thành công');
            if (selectedAttrId === attrId) closePanel();
            fetchList();
        } catch {
            showToast('error', 'Xoá thất bại', 2500);
        }
    };

    // === Update attribute name ===
    const handleSaveName = async () => {
        if (!canUpdate || !attrName.trim() || savingName) return;
        setSavingName(true);
        try {
            await updateAttribute(selectedAttrId, { name: attrName.trim() });
            showToast('success', 'Cập nhật tên thành công');
            fetchList();
        } catch (err) {
            showToast('error', err.response?.data?.message || 'Cập nhật thất bại', 2500);
        } finally {
            setSavingName(false);
        }
    };

    // === Add attribute value ===
    const handleAddValue = async () => {
        if (!canCreate || !newValue.trim()) return;
        try {
            await createAttributeValue({ attributeId: selectedAttrId, value: newValue.trim() });
            setNewValue('');
            fetchValues(selectedAttrId);
            showToast('success', 'Thêm giá trị thành công');
        } catch (err) {
            showToast('error', err.response?.data?.message || 'Thêm thất bại', 2500);
        }
    };

    // === Update attribute value ===
    const handleSaveValue = async () => {
        if (!canUpdate || !editingValueText.trim() || !editingValueId) return;
        try {
            await updateAttributeValue(editingValueId, { value: editingValueText.trim() });
            setEditingValueId(null);
            fetchValues(selectedAttrId);
            showToast('success', 'Cập nhật giá trị thành công');
        } catch (err) {
            showToast('error', err.response?.data?.message || 'Cập nhật thất bại', 2500);
        }
    };

    // === Delete attribute value ===
    const handleDeleteValue = async (valueId) => {
        if (!canDelete) return;
        if (!window.confirm('Xoá giá trị thuộc tính này?')) return;
        try {
            await deleteAttributeValue(valueId);
            fetchValues(selectedAttrId);
            showToast('success', 'Xoá giá trị thành công');
        } catch {
            showToast('error', 'Xoá thất bại', 2500);
        }
    };

    // === Create attribute (modal) ===
    const handleCreate = async () => {
        const trimmedName = createName.trim();
        const trimmedValues = createValues.filter((v) => v.trim());
        if (!trimmedName || trimmedValues.length === 0) {
            setCreateError('Vui lòng nhập tên và ít nhất 1 giá trị');
            return;
        }
        setCreating(true);
        setCreateError('');
        try {
            await createAttribute({ name: trimmedName, values: trimmedValues.map((v) => v.trim()) });
            showToast('success', 'Tạo thuộc tính thành công');
            setShowCreateModal(false);
            setCreateName('');
            setCreateValues(['']);
            fetchList();
        } catch (err) {
            setCreateError(err.response?.data?.message || 'Tạo thất bại');
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Thuộc tính</h1>
                    <p className={styles.subtitle}>{attributes.length} thuộc tính</p>
                </div>
                {canCreate && (
                    <button
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={() => {
                            setShowCreateModal(true);
                            setCreateName('');
                            setCreateValues(['']);
                            setCreateError('');
                        }}
                    >
                        <Plus /> Tạo thuộc tính
                    </button>
                )}
            </div>

            <div className={styles.layout}>
                {/* LEFT: Grid */}
                <div className={styles.layoutLeft}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className={styles.spinner} /> Đang tải...
                        </div>
                    ) : attributes.length === 0 ? (
                        <div className={styles.empty}>Chưa có thuộc tính nào</div>
                    ) : (
                        <div className={styles.grid}>
                            {attributes.map((attr) => (
                                <div
                                    key={attr.id}
                                    className={`${styles.card}${selectedAttrId === attr.id ? ` ${styles.cardActive}` : ''}`}
                                    onClick={() => openPanel(attr)}
                                >
                                    <span className={styles.cardName}>{attr.name}</span>
                                    {canDelete && (
                                        <button
                                            className={styles.deleteBtn}
                                            onClick={(e) => handleDeleteAttribute(e, attr.id)}
                                        >
                                            <Trash2 />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* RIGHT: Panel */}
                {selectedAttrId && (
                    <div className={styles.layoutRight}>
                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <h3 className={styles.panelTitle}>Giá trị thuộc tính</h3>
                                <button className={styles.panelClose} onClick={closePanel}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div className={styles.panelBody}>
                                {/* Edit name */}
                                {canUpdate && (
                                    <div className={styles.nameRow}>
                                        <input
                                            className={styles.nameInput}
                                            value={attrName}
                                            onChange={(e) => setAttrName(e.target.value)}
                                            placeholder="Tên thuộc tính"
                                        />
                                        <button
                                            className={styles.nameBtn}
                                            onClick={handleSaveName}
                                            disabled={savingName}
                                        >
                                            {savingName ? '...' : 'Lưu tên'}
                                        </button>
                                    </div>
                                )}

                                {/* Values list */}
                                <div className={styles.valuesTitle}>Danh sách giá trị</div>
                                {loadingValues ? (
                                    <div className={styles.loading}>
                                        <div className={styles.spinner} />
                                    </div>
                                ) : attrValues.length === 0 ? (
                                    <div className={styles.valuesEmpty}>Chưa có giá trị nào</div>
                                ) : (
                                    attrValues.map((val) => (
                                        <div key={val.id} className={styles.valueItem}>
                                            {editingValueId === val.id ? (
                                                <>
                                                    <input
                                                        className={styles.nameInput}
                                                        value={editingValueText}
                                                        onChange={(e) => setEditingValueText(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleSaveValue()}
                                                        autoFocus
                                                    />
                                                    <button className={styles.nameBtn} onClick={handleSaveValue}>
                                                        Lưu
                                                    </button>
                                                    <button
                                                        className={`${styles.valueBtn} ${styles.valueBtnDelete}`}
                                                        onClick={() => setEditingValueId(null)}
                                                    >
                                                        <X />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <span className={styles.valueText}>{val.value}</span>
                                                    {canUpdate && (
                                                        <button
                                                            className={`${styles.valueBtn} ${styles.valueBtnEdit}`}
                                                            onClick={() => {
                                                                setEditingValueId(val.id);
                                                                setEditingValueText(val.value);
                                                            }}
                                                        >
                                                            <Pencil />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            className={`${styles.valueBtn} ${styles.valueBtnDelete}`}
                                                            onClick={() => handleDeleteValue(val.id)}
                                                        >
                                                            <Trash2 />
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    ))
                                )}

                                {/* Add value */}
                                {canCreate && (
                                    <div className={styles.addValueRow}>
                                        <input
                                            className={styles.addValueInput}
                                            value={newValue}
                                            onChange={(e) => setNewValue(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddValue()}
                                            placeholder="Thêm giá trị mới..."
                                        />
                                        <button className={styles.addValueBtn} onClick={handleAddValue}>
                                            Thêm
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Tạo thuộc tính</h3>
                            <button className={styles.modalClose} onClick={() => setShowCreateModal(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            {createError && <div className={styles.formError}>{createError}</div>}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Tên thuộc tính <span>*</span>
                                </label>
                                <input
                                    className={styles.formInput}
                                    value={createName}
                                    onChange={(e) => setCreateName(e.target.value)}
                                    placeholder="VD: Màu sắc, Size..."
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Giá trị <span>*</span>
                                </label>
                                {createValues.map((v, idx) => (
                                    <div key={idx} className={styles.valueInputRow}>
                                        <input
                                            className={styles.formInput}
                                            value={v}
                                            onChange={(e) => {
                                                const next = [...createValues];
                                                next[idx] = e.target.value;
                                                setCreateValues(next);
                                            }}
                                            placeholder={`Giá trị ${idx + 1}`}
                                        />
                                        {createValues.length > 1 && (
                                            <button
                                                className={styles.removeValueBtn}
                                                onClick={() =>
                                                    setCreateValues(createValues.filter((_, i) => i !== idx))
                                                }
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    className={styles.addMoreBtn}
                                    onClick={() => setCreateValues([...createValues, ''])}
                                >
                                    <Plus size={14} /> Thêm giá trị
                                </button>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.modalBtn} onClick={() => setShowCreateModal(false)}>
                                Đóng
                            </button>
                            <button
                                className={`${styles.modalBtn} ${styles.modalBtnPrimary}`}
                                onClick={handleCreate}
                                disabled={creating}
                            >
                                {creating ? 'Đang tạo...' : 'Tạo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div
                            className={styles.toastIcon}
                            style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}
                        >
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
